// utils/copyRequest.js
//
// "Copy material" (My Materials → a composite request → Copy): a complete copy of a stored request —
// name, unit, description, category, visibility, cost-only, region and EVERY line item with its stored
// carbon / biogenic / weight / rate values — handed to the composite-requests page, which lands on
// Create New Material with it all filled in. The user must rename it before submitting.
//
// Unlike EditCompositeModal.reconstructEnvRows (which keeps only quantities and re-looks every value up),
// a copy keeps the stored values: the Build page's recalcEnvRow recomputes totals from them.

import { normKey } from './quantityLinks';

export const TYPE_BASE = { CO2: 'Co2', BIO: 'Co2-Biogenic', WEIGHT: 'Weight', RATE: 'Rate' };

// strict: "...-<BASE>" or "...-<BASE>-XX" only ("vquest-Co2-AU" is Co2, "vquest-Co2-Biogenic" is not)
export const typeMatchesBase = (type, base) => {
  const b = String(base).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(?:^|-)${b}(?:-[A-Z]{2})?$`, 'i');
  return re.test(String(type || ''));
};

const parseMaybe = (v) => {
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return v; }
};

/** The serialisable payload put in router state: { state: { copyRequest } }. */
export function buildCopyRequest(item) {
  const m = parseMaybe(item?.MaterialData) || {};
  const lineItems = parseMaybe(item?.LineItemsData);
  const name = String(m.Name || m.BaseMaterialName || '').trim();
  return {
    sourceRequestId: item?.RequestId || '',
    sourceName: name,
    regionId: item?.RegionId || '',
    name,
    description: m.Description || '',
    unit: m.QuantityTypeName || m.QuantityType || '',
    categoryId: m.CategoryId || m.Category || '',
    visibility: item?.Visibility || 'private',
    costOnly: !!m.CostOnly,
    lineItems: Array.isArray(lineItems) ? lineItems : [],
  };
}

const metricOf = (li) => ({
  measurementId: li.measurementId || '',
  code: li.code || '',
  unit: li.unit || '',
  value: Number(li.value) || 0,
  total: Number(li.total) || 0,
});

const separator = (uuid, isHeading, heading, subHeading, extra = {}) => ({
  id: uuid(),
  isHeading,
  isSubHeading: !isHeading,
  heading,
  subHeading: isHeading ? null : subHeading,
  category: '',
  description: '',
  quantity: 0,
  unit: '',
  carbon: {},
  bio: {},
  weight: {},
  locked: false,
  ...extra,
});

/**
 * Stored LineItemsData → the Build page's { rows, costRows, rowHistory }.
 * One env row per heading::sub-heading::description (submission writes one item per metric), heading
 * and sub-heading separator rows where they change, Rate items as cost rows linked by envRowId.
 */
export function rowsFromLineItems(lineItems, uuid) {
  const rows = [];
  const costRows = [];
  const rowHistory = {};
  const envByKey = new Map();
  let currentHeading = null;
  let currentSubHeading = null;

  for (const li of Array.isArray(lineItems) ? lineItems : []) {
    if (!li || typeof li !== 'object') continue;
    const heading = li.heading ?? li.headings ?? null;
    const subHeading = li.sub_heading ?? li.subHeading ?? null;
    if (li.isHeading || li.isSubHeading) continue; // legacy separator items carry no values

    const isRate = typeMatchesBase(li.measurementId || li.code, TYPE_BASE.RATE);
    // a block copied in by "+ Add Assembly" (see utils/expandAssembly) — keep it a block
    const assemblyBlock = li.assembly_source && li.assembly_source.blockId ? li.assembly_source : null;
    const blockExtra = assemblyBlock ? { assemblyBlock } : {};
    const description = String(li.description || '').trim();
    const key = `${heading || ''}::${subHeading || ''}::${description}`;

    if (isRate) {
      const env = envByKey.get(key);
      costRows.push({
        id: uuid(),
        envRowId: env?.id,
        category: li.category || '',
        description,
        quantity: Number(li.quantity) || 0,
        unit: li.unit || '',
        rate: metricOf(li),
        locked: false,
        heading,
        ...blockExtra,
      });
      continue;
    }

    let env = envByKey.get(key);
    if (!env) {
      if (heading && heading !== currentHeading) {
        currentHeading = heading;
        currentSubHeading = null;
        rows.push(separator(uuid, true, heading, null, assemblyBlock ? { fromAssembly: assemblyBlock, assemblyBlock } : {}));
      }
      if (subHeading && String(subHeading).trim() !== '' && subHeading !== currentSubHeading) {
        currentSubHeading = subHeading;
        rows.push(separator(uuid, false, currentHeading, subHeading, blockExtra));
      }
      env = {
        id: uuid(),
        category: li.category || '',
        description,
        quantity: Number(li.quantity) || 0,
        unit: li.unit || '',
        carbon: {},
        bio: {},
        weight: {},
        locked: false,
        isHeading: false,
        isSubHeading: false,
        heading,
        subHeading,
        linkKey: normKey(key),
        ...blockExtra,
      };
      envByKey.set(key, env);
      rows.push(env);
    }

    const type = li.measurementId || li.code;
    if (typeMatchesBase(type, TYPE_BASE.BIO)) env.bio = metricOf(li);
    else if (typeMatchesBase(type, TYPE_BASE.CO2)) env.carbon = metricOf(li);
    else if (typeMatchesBase(type, TYPE_BASE.WEIGHT)) env.weight = metricOf(li);

    if (!rowHistory[env.id] && Array.isArray(li.line_item_rows) && li.line_item_rows.length > 0) {
      rowHistory[env.id] = li.line_item_rows;
    }
  }

  // a Rate item stored BEFORE its env row's items: link it now
  for (const cr of costRows) {
    if (cr.envRowId) continue;
    const env = rows.find(r => !r.isHeading && !r.isSubHeading && r.description === cr.description);
    if (env) cr.envRowId = env.id;
  }

  return { rows, costRows, rowHistory };
}

/** The category to preselect: the stored id when this region has it, else a name match (legacy rows), else ''. */
export function resolveCopyCategory(categoryIdOrName, categories) {
  const list = Array.isArray(categories) ? categories : [];
  const v = String(categoryIdOrName || '').trim();
  if (!v) return '';
  const byId = list.find(c => String(c.id) === v);
  if (byId) return byId.id;
  const byName = list.find(c => String(c.name || '').trim().toLowerCase() === v.toLowerCase());
  return byName ? byName.id : '';
}

/** True while the copy still carries its source's name (the user must rename it). */
export const isUnrenamedCopy = (name, sourceName) =>
  !!sourceName && String(name || '').trim().toLowerCase() === String(sourceName).trim().toLowerCase();
